import { Entypo, FontAwesome5, Ionicons, MaterialIcons } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import * as WebBrowser from "expo-web-browser";
import { Locale } from "locale-enum";
import {
  Box,
  Button,
  Center,
  Circle,
  Divider,
  Heading,
  HStack,
  Icon,
  Image,
  Pressable,
  Progress,
  Radio,
  ScrollView,
  Select,
  Text,
  View,
  VStack,
} from "native-base";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SafeAreaView } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import * as URI from "uri-js";

import { ASYNC_STORAGE__LANGUAGE_KEY } from "../../../i18n";
import { USER_LOGOUT_ACTION } from "../../../store";
import BlogLinksDropdown from "../components/BlogLinksDropdown";
import CustomBrandingMobileScreenHeader from "../components/CustomBrandingMobileScreenHeader";
import MenstrualCycleDiagram from "../components/MenstrualCycleDiagram";
import { BUILD_NUMBER, commonStyles, Images, isDesktopScreen, Routes, width as deviceWidth } from "../constants";
import { Scale, VerticalScale, width } from "../constants/helperFunction";
import { calculateDailyTotalForMacro } from "../helpers/coachHelpers";
import { FeatureFlag, isFeatureFlagActive } from "../helpers/featureFlags";
import { apiCallErrorHandler, isMobilePlatform } from "../helpers/generalHelpers";
import { logout } from "../helpers/logout";
import { ContactSupportLink, StripeCustomerPortalLink } from "../helpers/supportHelpers";
import {
  doesTheUserHaveMenstrualPlanSet,
  getMenstrualCyclePropsFromUser,
  hasUserCompletedTheirDiyNutritionPlanningFormCompletely,
  isUserDiy,
  isUserInPaymentRequiredOrganisation,
  organisationIsDiyBillingForUsers,
} from "../helpers/userHelpers";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import logger from "../services/logger";
import { onboardingSlice } from "../slices/onboardingSlice";
import { clientModeSelector, userSelector, userSlice } from "../slices/userSlice";
import styles from "./MyProfileScreenStyles";

const {
  useUsersUserDestroyMutation,
  useCheckoutSubscriptionVerificationCreateMutation,
  useUsersUserProfileUpdateMutation,
} = backendApi;

type SupportedLanguage = Locale.en | Locale.nl;
const LANGUAGE_OPTIONS: SupportedLanguage[] = [Locale.en, Locale.nl];

type Props = NativeStackScreenProps<RootStackParamList, Routes.MyProfileScreen>;
const MyProfileScreen = ({ navigation }: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const isDesktop = isDesktopScreen();
  const isWeb = !isMobilePlatform();

  const user = useSelector(userSelector);
  const clientMode = useSelector(clientModeSelector);

  const [deleteUserOnBackend] = useUsersUserDestroyMutation();

  const [updateUserProfile, { isLoading: isUpdatingUserProfile }] = useUsersUserProfileUpdateMutation();

  // NOTE: This feature was here for app store compliance.
  // Since we are not updating our app store app this is here only for reference.
  const deleteAccountButtonPress = async (): Promise<void> => {
    if (!user) {
      throw new Error("User is not defined");
    }

    const proceedToNextStep = window.confirm(t("delete_account.warning"));

    if (!proceedToNextStep) {
      return;
    }

    const confirmKeyword = t("delete_account.confirm_keyword");
    const returnValue = prompt(t("delete_account.input_confirmation", { confirmKeyword }));

    if (returnValue === confirmKeyword) {
      await deleteUserOnBackend({
        id: user.id,
      });

      logout(dispatch);
    } else {
      alert(t("delete_account.abandon", { confirmKeyword }));
    }
  };

  const swapMode = (): void => {
    dispatch(userSlice.actions.toggleClientMode());
  };

  const swapModeButton = (
    <Button
      leftIcon={<Icon as={MaterialIcons} name="swap-horiz" size="sm" />}
      mt={2}
      onPress={swapMode}
      testID={"swapMode-Button"}
      nativeID={"swapModeButton"}
    >
      {t("coach_mode_settings.swap_mode_button_text", {
        modeName: t(`coach_mode_settings.${clientMode ? "coach_mode_label_text" : "client_mode_label_text"}`),
      })}
    </Button>
  );

  const formatLanguage = (language: SupportedLanguage): string => t(`general.locales.${language}`);

  const switchLanguage = async (language: Locale): Promise<void> => {
    await i18n.changeLanguage(language);

    await AsyncStorage.setItem(ASYNC_STORAGE__LANGUAGE_KEY, language);
    dispatch(userSlice.actions.storeLocale(language));
  };

  const redoNutritionPlanningButton = (
    <Button
      leftIcon={<Icon as={MaterialIcons} name="refresh" size="sm" />}
      mt={4}
      mb={2}
      onPress={() => {
        if (!user) {
          throw new Error("User is not defined");
        }
        if (!user.intake) {
          throw new Error("User intake is not defined");
        }
        // This ensures that the state used for the questions will be set appropriately
        dispatch(onboardingSlice.actions.setUserProfileData(user.intake));

        navigation.push(Routes.DIY1GoalScreen);
      }}
      testID={"redoNutritionPlanning-Button"}
      nativeID={"redoNutritionPlanningButton"}
      colorScheme="primary"
      size="md"
      width="100%"
    >
      {t("my_profile.redo_nutrition_planning_button_text")}
    </Button>
  );

  const shouldShowStripeCustomerPortalLink =
    (!clientMode && user && isWeb) || (user && organisationIsDiyBillingForUsers(user));

  // Format food intolerances for display
  const formatIntolerances = (): string => {
    const intolerances = [];

    if (user?.intake?.food_intolerances_gluten) {
      intolerances.push(t("general.intolerances_and_allergies.GLUTEN"));
    }
    if (user?.intake?.food_intolerances_lactose) {
      intolerances.push(t("general.intolerances_and_allergies.LACTOSE"));
    }
    if (user?.intake?.food_intolerances_nut) {
      intolerances.push(t("general.intolerances_and_allergies.NUTS"));
    }
    if (user?.intake?.food_intolerances_crustaceans_shellfish) {
      intolerances.push(t("general.intolerances_and_allergies.CRUSTACEANS_AND_SHELLFISH"));
    }

    if (intolerances.length === 0) {
      return t("onboarding.onboarding_9_intolerance_screen.no_intolerance");
    }

    return intolerances.join(", ");
  };

  // Format disliked ingredients for display
  const formatDislikedIngredients = (): string => {
    if (!user?.ingredients_to_avoid || user.ingredients_to_avoid.length === 0) {
      return t("general.none");
    }

    return user.ingredients_to_avoid.join(", ");
  };

  // Get dietary preference display name
  const getDietaryPreference = (): string => {
    if (!user?.intake?.diet) {
      return t("general.diets.OMNIVORE");
    }
    return t(`general.diets.${user.intake.diet}`);
  };

  const isOnMenopauseDiet = (): boolean => !!user?.intake?.is_on_menopause_diet;

  // Get goal display name
  const getGoal = (): string | undefined => {
    if (user?.intake?.main_goal) {
      return t(`general.goals.${user.intake.main_goal}`);
    }
    return undefined;
  };

  // Helper function to format height according to user preferences
  const formatHeight = (): string => {
    if (!user?.intake?.height_in_cm) return "-";

    // Check if user prefers imperial units for height
    const usesImperial = user.intake.uses_imperial_measurements_for_height;

    if (usesImperial) {
      // Convert cm to feet and inches
      const heightInInches = user.intake.height_in_cm / 2.54;
      const feet = Math.floor(heightInInches / 12);
      const inches = Math.round(heightInInches % 12);

      // Handle the case where inches is 12 (should roll over to next foot)
      if (inches === 12) {
        return `${feet + 1}' 0"`;
      }

      return `${feet}' ${inches}"`;
    }
    // Display in centimeters
    return `${user.intake.height_in_cm} ${t("onboarding.onboarding_5_physical_stats_screen.centimeters")}`;
  };

  // Helper function to format weight according to user preferences
  const formatWeight = (weightInKg: number | undefined | null): string => {
    if (!weightInKg) return "-";

    const weightUnit = user?.intake?.personal_weight_unit || "KILOGRAMS";

    if (weightUnit === "POUNDS") {
      const pounds = Math.round(weightInKg * 2.20462);
      return `${pounds} ${t("general.pounds")}`;
    }
    if (weightUnit === "STONES") {
      const totalPounds = weightInKg * 2.20462;
      const stones = Math.floor(totalPounds / 14);
      const pounds = Math.round(totalPounds % 14);
      return `${stones} st ${pounds} ${t("general.pounds")}`;
    }
    return `${weightInKg} ${t("onboarding.onboarding_5_physical_stats_screen.kilograms")}`;
  };

  const handleRecipeUnitChange = async (useImperial: boolean): Promise<void> => {
    if (!user || !user.intake) return;

    try {
      const userProfileRequest = {
        ...user.intake,
        uses_imperial_measurements_for_recipes: useImperial,
      };

      await updateUserProfile({
        id: user.intake.id,
        userProfileRequest,
      })
        .unwrap()
        .then(() => {
          alert(t("my_profile.recipe_units_updated_successfully"));
        })
        .catch(apiCallErrorHandler());
    } catch (error) {
      logger.error("Failed to update recipe units preference", error);
    }
  };

  const menstrualCycleProps = user ? getMenstrualCyclePropsFromUser(user) : undefined;

  async function handlePeriodStartedEarly(): Promise<void> {
    if (!user || !user.intake) return;

    const today = new Date();
    const formattedDate = today.toISOString().split("T")[0]; // Format as YYYY-MM-DD

    try {
      const userProfileRequest = {
        ...user.intake,
        last_menstrual_period_start_date: formattedDate,
      };

      await updateUserProfile({
        id: user.intake.id,
        userProfileRequest,
      })
        .unwrap()
        .then(() => {
          alert(t("my_profile.menstrual_cycle.period_start_date_updated"));
        })
        .catch(apiCallErrorHandler());
    } catch (error) {
      logger.error("Failed to update menstrual period start date", error);
    }
  }

  const UserProfile = (): JSX.Element => (
    <ScrollView showsVerticalScrollIndicator={false}>
      {/* Header Section */}
      <VStack space={4} mb={6} alignItems="center">
        {/* NOTE: We do not currently populate the user's name in the payment flow so this is never available */}
        {/* <VStack alignItems="center" space={1} mt={4}>
          <Heading fontSize="xl">
            <Text>{`${user?.first_name} ${user?.last_name}`}</Text>
          </Heading>
        </VStack> */}

        {/* Blog Links Dropdown */}
        {isFeatureFlagActive(user, FeatureFlag.FF_FEMALE_HORMONES) ? (
          <HStack justifyContent="center">
            <BlogLinksDropdown />
          </HStack>
        ) : null}

        {/* Compact Help Button */}
        <HStack width="100%" justifyContent="center" mt={2}>
          <ContactSupportLink user={user} width="auto" size="lg" mt={1} shadow={1} />
        </HStack>

        {/* Language Selector with Label */}
        <HStack width="60%" mt={2} space={2} alignItems="center">
          <Text fontWeight="medium">
            {t("my_profile.language_chooser.section_title")}
            {":"}
          </Text>
          <Box flex={1}>
            <Select
              defaultValue={i18n.language}
              accessibilityLabel={t("my_profile.language_chooser.language_select_placeholder")}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onValueChange={async (language) => switchLanguage(language as Locale)}
              testID="language-selector"
            >
              {LANGUAGE_OPTIONS?.map((language) => (
                <Select.Item key={language} label={formatLanguage(language)} value={language} />
              ))}
            </Select>
          </Box>
        </HStack>
      </VStack>

      <Divider mb={4} />

      {/* Nutrition Section */}
      <Box bg="white" rounded="md" shadow={4} p={4} mb={4} borderWidth={1} borderColor="gray.200">
        <Heading size="md" mb={2}>
          {t("my_profile.macros_heading")}
        </Heading>
        <Divider mb={4} bg="gray.200" />

        {user?.intake ? (
          <VStack space={4}>
            {/* Macros display */}
            <HStack justifyContent="space-between" space={2} width="100%">
              {/* Calories Box */}
              <Box flex={1} bg="gray.100" p={3} rounded="md" alignItems="center" testID="macro-box-calories">
                <Text color="gray.500" fontSize="xs">
                  {t("general.calories")}
                </Text>
                <Text fontWeight="bold" fontSize="md">
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                    ? calculateDailyTotalForMacro(
                        "kcal",
                        user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                      )
                    : "-"}
                </Text>
                <Text fontSize="xl">🔥</Text>
              </Box>

              {/* Protein Box */}
              <Box flex={1} bg="gray.100" p={3} rounded="md" alignItems="center" testID="macro-box-protein">
                <Text color="gray.500" fontSize="xs">
                  {t("general.protein_short")}
                </Text>
                <Text fontWeight="bold" fontSize="md">
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                    ? calculateDailyTotalForMacro(
                        "protein",
                        user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                      )
                    : "-"}
                </Text>
                <Text fontSize="xl">💪</Text>
              </Box>

              {/* Fat Box */}
              <Box flex={1} bg="gray.100" p={3} rounded="md" alignItems="center" testID="macro-box-fat">
                <Text color="gray.500" fontSize="xs">
                  {t("general.fat")}
                </Text>
                <Text fontWeight="bold" fontSize="md">
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                    ? calculateDailyTotalForMacro(
                        "fat",
                        user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                      )
                    : "-"}
                </Text>
                <Text fontSize="xl">🥑</Text>
              </Box>

              {/* Carbs Box */}
              <Box flex={1} bg="gray.100" p={3} rounded="md" alignItems="center" testID="macro-box-carbs">
                <Text color="gray.500" fontSize="xs">
                  {t("general.carbohydrates_short")}
                </Text>
                <Text fontWeight="bold" fontSize="md">
                  {user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                    ? calculateDailyTotalForMacro(
                        "carbohydrates",
                        user.intake.weekly_nutrition_plan?.monday.meal_slot_specifications
                      )
                    : "-"}
                </Text>
                <Text fontSize="xl">🍞</Text>
              </Box>
            </HStack>
          </VStack>
        ) : null}
      </Box>

      {/* Goal & Biometrics Section */}
      <Box bg="white" rounded="md" shadow={4} p={4} mb={4} borderWidth={1} borderColor="gray.200">
        <Heading size="md" mb={2}>
          {t("my_profile.profile_heading")}
        </Heading>
        <Divider mb={4} bg="gray.200" />

        <VStack space={3}>
          <HStack justifyContent="space-between">
            <Text fontWeight="semibold">{t("general.email_address")}</Text>
            <Text>{user?.email}</Text>
          </HStack>

          {getGoal() ? (
            <HStack justifyContent="space-between">
              <Text fontWeight="semibold">{t("coach_mode_create_new_client.goal_title_text")}</Text>
              <Text>{getGoal()}</Text>
            </HStack>
          ) : null}

          {/* NOTE: I don't think this is useful to show to the user */}
          {/* <HStack justifyContent="space-between">
            <Text fontWeight="semibold">{t("general.activity.ACTIVE")}</Text>
            <Text>{getActivityLevel()}</Text>
          </HStack> */}

          <HStack justifyContent="space-between">
            <Text fontWeight="semibold">{t("coach_mode_create_new_client.gender_title_text")}</Text>
            <Text>{user?.intake?.gender ? t(`general.gender.${user.intake.gender}`) : "-"}</Text>
          </HStack>

          <HStack justifyContent="space-between">
            <Text fontWeight="semibold">{t("coach_mode_create_new_client.age_title_text")}</Text>
            <Text>{user?.intake?.age || "-"}</Text>
          </HStack>

          <HStack justifyContent="space-between">
            <Text fontWeight="semibold">{t("coach_mode_create_new_client.height_title_text")}</Text>
            <Text>{formatHeight()}</Text>
          </HStack>

          <HStack justifyContent="space-between">
            <Text fontWeight="semibold">{t("coach_mode_create_new_client.weight_title_text")}</Text>
            <Text>{formatWeight(user?.intake?.weight)}</Text>
          </HStack>

          {/* NOTE: I am not sure this is stored on the DB for everyone */}
          {/* This is not relevant for everyone because some people want to maintain their weight */}
          {user?.intake?.goal_weight_in_kg ? (
            <HStack justifyContent="space-between">
              <Text fontWeight="semibold">{t("onboarding.onboarding_5_physical_stats_screen.goal_weight_title")}</Text>
              <Text>{formatWeight(user?.intake?.goal_weight_in_kg)}</Text>
            </HStack>
          ) : null}
        </VStack>
      </Box>

      {/* Dietary Preferences Section */}
      <Box bg="white" rounded="md" shadow={4} p={4} mb={4} borderWidth={1} borderColor="gray.200">
        <Heading size="md" mb={2}>
          {t("coach_mode_create_new_client.diet_title_text")}
        </Heading>
        <Divider mb={4} bg="gray.200" />
        <VStack space={3}>
          <HStack justifyContent="space-between">
            <Text fontWeight="semibold">{t("coach_mode_create_new_client.diet_title_text")}</Text>
            <Text>{getDietaryPreference()}</Text>
          </HStack>

          {isOnMenopauseDiet() && (
            <HStack justifyContent="space-between">
              <Text fontWeight="semibold">{t("onboarding.onboarding_7_nutrition_plan_screen.menopause")}</Text>
              <Text>{t("onboarding.onboarding_menstrual_cycle_spacing_screen.yes")}</Text>
            </HStack>
          )}

          <HStack alignItems="flex-start" justifyContent="space-between">
            <Text fontWeight="semibold">{t("coach_mode_create_new_client.intolerances_and_allergies_title_text")}</Text>
            <Text textAlign="right" flex={1} ml={2}>
              {formatIntolerances()}
            </Text>
          </HStack>

          <HStack alignItems="flex-start" justifyContent="space-between">
            <Text fontWeight="semibold">{t("onboarding.onboarding_10_ingredient_dislikes_screen.title")}</Text>
            <Text textAlign="right" flex={1} ml={2}>
              {formatDislikedIngredients()}
            </Text>
          </HStack>
        </VStack>
      </Box>

      {/* Menstrual Cycle Section - Only shown if relevant */}
      {user && doesTheUserHaveMenstrualPlanSet(user) ? (
        <Box bg="white" rounded="md" shadow={4} p={4} mb={4} borderWidth={1} borderColor="gray.200">
          <Heading size="md" mb={2}>
            {t("my_profile.menstrual_cycle.heading")}
          </Heading>
          <Divider mb={4} bg="gray.200" />

          {/* Add the cycle diagram */}
          {user && menstrualCycleProps !== undefined ? (
            <Box alignItems="center" mb={4}>
              <MenstrualCycleDiagram {...menstrualCycleProps} />
            </Box>
          ) : null}

          <VStack space={3}>
            {user?.intake?.last_menstrual_period_start_date ? (
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">{t("my_profile.menstrual_cycle.last_period_start")}</Text>
                <Text>
                  {new Date(user.intake.last_menstrual_period_start_date).toLocaleDateString(
                    i18n.language.startsWith("nl") ? "nl-NL" : "en-US",
                    { year: "numeric", month: "short", day: "numeric" }
                  )}
                </Text>
              </HStack>
            ) : null}

            {user?.intake?.menstrual_cycle_length ? (
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">{t("my_profile.menstrual_cycle.cycle_length")}</Text>
                <Text>
                  {user.intake.menstrual_cycle_length}{" "}
                  {t("onboarding.onboarding_menstrual_cycle_spacing_screen.days").toLowerCase()}
                </Text>
              </HStack>
            ) : null}

            {/* Period Started Early Button - Only shown for users on cycle diet */}
            {user && doesTheUserHaveMenstrualPlanSet(user) ? (
              <Button
                mt={4}
                colorScheme="primary"
                size="md"
                width="auto"
                alignSelf="center"
                leftIcon={<Icon as={MaterialIcons} name="event" size="sm" />}
                onPress={() => {
                  handlePeriodStartedEarly().catch((err) => {
                    logger.error("Failed to update menstrual period start date", err);
                    alert(t("general.generic_error_message"));
                    throw err;
                  });
                }}
                testID="period-started-early-button"
              >
                {t("my_profile.menstrual_cycle.period_started_early")}
              </Button>
            ) : null}
          </VStack>
        </Box>
      ) : null}

      {/* Units & Preferences Section - with radio button toggle */}
      <Box bg="white" rounded="md" shadow={4} p={4} mb={4} borderWidth={1} borderColor="gray.200">
        <Heading size="md" mb={2}>
          {t("my_profile.units_heading")}
        </Heading>
        <Divider mb={4} bg="gray.200" />
        <VStack space={3}>
          <VStack space={2}>
            <Text fontWeight="semibold">{t("my_profile.recipe_units")}</Text>
            <Radio.Group
              name="recipeUnits"
              value={user?.intake?.uses_imperial_measurements_for_recipes ? "imperial" : "metric"}
              onChange={(value) => handleRecipeUnitChange(value === "imperial")}
              isDisabled={isUpdatingUserProfile}
            >
              <HStack space={4}>
                <Radio value="metric" my={1}>
                  {t("my_profile.metric_units")}
                </Radio>
                <Radio value="imperial" my={1}>
                  {t("my_profile.imperial_units")}
                </Radio>
              </HStack>
            </Radio.Group>
            {isUpdatingUserProfile ? <Progress size="xs" mt={2} /> : null}
          </VStack>
        </VStack>
      </Box>

      {/* Redo Nutrition Planning Button */}
      {user && isUserDiy(user) && clientMode && hasUserCompletedTheirDiyNutritionPlanningFormCompletely(user) ? (
        <Box mb={4}>{redoNutritionPlanningButton}</Box>
      ) : null}

      {/* Standalone Subscription Button */}
      {shouldShowStripeCustomerPortalLink ? (
        <Box mb={4}>
          <StripeCustomerPortalLink isDesktop={isDesktop} />
        </Box>
      ) : null}

      {/* Standalone Coach Mode Button */}
      {user?.is_coach ? <Box mb={4}>{swapModeButton}</Box> : null}

      {/* Logout Button - Standalone */}
      <Button
        onPress={() => logout(dispatch)}
        testID={"logout-button"}
        nativeID={"logoutButton"}
        width="100%"
        variant="outline"
        colorScheme="gray"
        size="md"
        my={4}
        leftIcon={<Icon as={MaterialIcons} name="logout" size="sm" color="gray.500" />}
      >
        <Text color="gray.600">{t("general.logout")}</Text>
      </Button>

      {/* NOTE: This was more relevant when we maintained a mobile app */}
      {/* <Center mt={2} mb={8}>
        <Text fontSize="xs" color="gray.500">
          {BUILD_NUMBER}
        </Text>
      </Center> */}
    </ScrollView>
  );

  return (
    <SafeAreaView style={commonStyles.container}>
      {!isDesktop && <CustomBrandingMobileScreenHeader />}

      <Box flex={1} p={4} bg="white">
        <UserProfile />
      </Box>
    </SafeAreaView>
  );
};

export default MyProfileScreen;
